// ===================== Hex grid primitives =====================
// Axial coordinates (q, r) on a pointy-top hex grid — the layout used by the
// world map. Pure math with no game or DOM knowledge: coordinates in, keys and
// pixels out. See https://www.redblobgames.com/grids/hexagons/ for the model.

// The six axial neighbour offsets, in clockwise order starting east.
export const HEX_DIRS = [
  [1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1],
];

// Canonical string id for a hex — used as the save key and DOM data attribute.
export function hexKey(q, r) { return `${q},${r}`; }

export function parseHexKey(key) {
  const [q, r] = String(key).split(',').map(Number);
  return { q, r };
}

export function hexNeighbors(q, r) {
  return HEX_DIRS.map(([dq, dr]) => ({ q: q + dq, r: r + dr }));
}

// Number of steps between two hexes (axial → cube distance).
export function hexDistance(aq, ar, bq, br) {
  const dq = aq - bq, dr = ar - br;
  return (Math.abs(dq) + Math.abs(dq + dr) + Math.abs(dr)) / 2;
}

export function hexAdjacent(aq, ar, bq, br) {
  return hexDistance(aq, ar, bq, br) === 1;
}

// The `radius`-step ring around a centre (radius 0 → just the centre).
export function hexRing(q, r, radius) {
  if (radius <= 0) return [{ q, r }];
  const out = [];
  // Walk to the ring's starting corner, then trace each of the six sides.
  let cq = q + HEX_DIRS[4][0] * radius;
  let cr = r + HEX_DIRS[4][1] * radius;
  for (let side = 0; side < 6; side++) {
    for (let step = 0; step < radius; step++) {
      out.push({ q: cq, r: cr });
      cq += HEX_DIRS[side][0];
      cr += HEX_DIRS[side][1];
    }
  }
  return out;
}

// Centre point of a hex in pixel space (pointy-top, `size` = centre→corner).
export function hexToPixel(q, r, size = 1) {
  return {
    x: size * Math.sqrt(3) * (q + r / 2),
    y: size * 1.5 * r,
  };
}

// The six corner points of a pointy-top hex, ready for an SVG polygon.
export function hexCorners(cx, cy, size) {
  const pts = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i - 30);
    pts.push([cx + size * Math.cos(a), cy + size * Math.sin(a)]);
  }
  return pts;
}

// Corner list as an SVG `points` attribute string.
export function hexPoints(cx, cy, size, precision = 2) {
  return hexCorners(cx, cy, size)
    .map(([x, y]) => `${x.toFixed(precision)},${y.toFixed(precision)}`)
    .join(' ');
}
